/**
 * UUID62 Conversion Utilities
 *
 * This module provides functions to convert between UUIDv4 and UUID62 formats:
 * - UUIDv4: Standard 36-character UUID with hyphens (e.g., 123e4567-e89b-12d3-a456-426614174000)
 * - UUID62: Compact representation using base62 encoding (0-9, a-z, A-Z)
 *
 * This implementation is designed for PCI DSS compliant applications with security in mind.
 *
 * Compatible with ES2023 and deployed as an ESM application using Vite.
 */

// Base62 character set (0-9, a-z, A-Z)
const BASE62_CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Validates if a string is a valid UUIDv4
 * @param uuid The UUID string to validate
 * @returns true if the string is a valid UUIDv4, false otherwise
 */
function isValidUuidV4(uuid: string): boolean {
    if (!uuid) return false;

    const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidV4Regex.test(uuid);
}

/**
 * Validates if a string is a valid UUID62
 * @param uuid62 The UUID62 string to validate
 * @returns true if the string is a valid UUID62, false otherwise
 */
function isValidUuid62(uuid62: string): boolean {
    if (!uuid62) return false;

    // UUID62 should only contain characters from the base62 character set
    const uuid62Regex = new RegExp(`^[${BASE62_CHARS}]{22}$`);
    return uuid62Regex.test(uuid62);
}

/**
 * Encodes a UUIDv4 string into a UUID62 string
 * @param uuid The UUIDv4 string to encode
 * @returns The UUID62 encoded string
 * @throws Error if the input is not a valid UUIDv4
 */
export function encode(uuid: string): string {
    if (!uuid) return null;

    // Validate input
    if (!isValidUuidV4(uuid)) {
        throw new Error('Invalid UUIDv4 format');
    }

    // Remove hyphens (using ES2021's replaceAll method)
    const hexString = uuid.replaceAll('-', '');

    // Convert from hex to decimal (bigint)
    let decimal = 0n;
    for (let i = 0; i < hexString.length; i++) {
        decimal = decimal * 16n + BigInt(parseInt(hexString[i]!, 16));
    }

    // Convert from decimal to base62
    let result = '';
    while (decimal > 0n) {
        const remainder = Number(decimal % 62n);
        result = BASE62_CHARS[remainder] + result;
        decimal = decimal / 62n;
    }

    // UUID62 should be 22 characters long, pad if necessary
    return result.padStart(22, '0');
}

/**
 * Decodes a UUID62 string back to a UUIDv4 string
 * @param uuid62 The UUID62 string to decode
 * @returns The UUIDv4 string
 * @throws Error if the input is not a valid UUID62
 */
export function decode(uuid62: string): string {
    // Validate input
    if (!isValidUuid62(uuid62)) {
        throw new Error('Invalid UUID62 format');
    }

    // Convert from base62 to decimal (bigint)
    let decimal = 0n;
    for (let i = 0; i < uuid62.length; i++) {
        decimal = decimal * 62n + BigInt(BASE62_CHARS.indexOf(uuid62[i]!));
    }

    // Convert from decimal to hex
    let hexString = '';
    while (decimal > 0n) {
        const remainder = Number(decimal % 16n);
        hexString = remainder.toString(16) + hexString;
        decimal = decimal / 16n;
    }

    // Ensure the hex string is 32 characters (16 bytes)
    hexString = hexString.padStart(32, '0');

    // Format with hyphens (8-4-4-4-12 format)
    return [
        hexString.slice(0, 8),
        hexString.slice(8, 12),
        hexString.slice(12, 16),
        hexString.slice(16, 20),
        hexString.slice(20, 32)
    ].join('-');
}

// Example with explicit type annotations for better TypeScript safety
// const originalUuid: string = '123e4567-e89b-12d3-a456-426614174000';
// const encoded: string = encode(originalUuid);
// console.log('Encoded:', encoded);
// const decoded: string = decode(encoded);
// console.log('Decoded:', decoded);

export default {
    encode,
    decode
};